import angular from 'angular';
import reconItemsTemplate from './reconItems.template.html';

/*******************************************************************************
 * AccountingFilter */
/**
 * @ngdoc   filter
 * @module  csm.books.BankRecon
 * @name    csm.books.BankRecon.filter:accounting
 * @description
 * Creates a filter based off of the currency filter to display negative numbers
 * with parentheses instead of a minus sign. This requires using the ng-bind-htm
 * attribute.
 *******************************************************************************/
/* @ngInject */ export function AccountingFilter($filter, $sce) {
  return function (amount, currencySymbol, precision) {
    var currency = $filter('currency');
    amount = amount + ' ';

    if (amount.charAt(0) === '-')
      return $sce.trustAs(
        'html',
        currency(amount, currencySymbol, precision).replace('-', '(') + ')'
      );

    return $sce.trustAs(
      'html',
      currency(amount, currencySymbol, precision) + '&nbsp;'
    );
  };
}

/*******************************************************************************
 * Component: ReconItemsComponent */
/**
 * @ngdoc   component
 * @module  csm.books.BankRecon
 * @name    csm.books.BankRecon.component:reconItems
 *
 * @param  {Object[]}  items              Bank Statement items to display.
 * @param  {Object}      items.entryDate  Date of the line item.
 * @param  {String}      items.type       Line Item type.
 * @param  {String}      items.docNumber  Receipt/Check number.
 * @param  {String}      items.desc       Item description.
 * @param  {Number}      items.amount     Line Item amount.
 * @param  {Boolean}     items.cleared    Line Item is cleared for the
 *                                        reconciliation.
 * @param  {Boolean}     items.isDebit    Line Item is on the debit side.
 * @param  {Boolean=}  disableEdit        Locks the grids from making changes.
 * @description
 * Displays Bank Statement items in two ui-grids. One for the credits (checks),
 * and one for the debits (deposits). The grids will be displayed side-by-side
 * on large screens, and stacked on smaller screens.
 * <p />
 * Any item that cannot be cleared will not be selectable, and will be grayed
 * out and italicized.
 * <p />
 * The disableEdit flag will prevent any selections from being changed.
 *******************************************************************************/
export const reconItemsComponent = {
  template: reconItemsTemplate,
  controller: ReconItemsController,
  bindings: {
    items: '=',
    disableEdit: '<'
  }
};

/*******************************************************************************
 * Controller: ReconItemsController */
/**
 * @ngdoc   controller
 * @module  csm.books.BankRecon
 * @name    csm.books.BankRecon.controller:BankReconItems
 * @description
 * Controller for the {@link csm.books.BankRecon.component:reconItems reconItems}
 * component.
 *******************************************************************************/
/* @ngInject */ function ReconItemsController(
  $scope,
  $timeout,
  SERVER,
  uiGridConstants
) {
  /** Self Reference. */ var mThis = this;
  /** Checks label. */ mThis.checksLabel = SERVER.BooksWebConstants.checksLabel;
  /** Deposits label. */ mThis.depositsLabel =
    SERVER.BooksWebConstants.depositsLabel;

  /** Check items. */ var mCheckItems = [];
  /** Deposit items. */ var mDepositItems = [];

  /** Credits grid Type column filter. */
  var mCheckTypeFilter = {
    term: null,
    type: uiGridConstants.filter.SELECT,
    condition: uiGridConstants.filter.EXACT
  };
  /** Debits grid Type column filter. */
  var mDepositTypeFilter = {
    term: null,
    type: uiGridConstants.filter.SELECT,
    condition: uiGridConstants.filter.EXACT
  };

  /** Watch for changes to the bound items property. */
  $scope.$watch(
    function () {
      return mThis.items;
    },
    function (newValue, oldValue) {
      if (newValue == oldValue) return;

      mCheckItems = mThis.items.map(function (item) {
        return !item.isDebit;
      });
      mDepositItems = mThis.items.map(function (item) {
        return item.isDebit;
      });

      filterItems();
      setTypeFilters();
    }
  );

  /*****************************************************************************
   * getGridOptions */
  /**
   * Defines UI grid options to be used with the statement items. This will handle
   * the processing of rows being selected/unselected.
   *
   * @param  forDeposit  Determines if this will be for the deposits grid or the
   *                     checks grid.
   * @return             Returns the object to be used with the UI grid.
   *****************************************************************************/
  function getGridOptions(forDeposit) {
    var gridOptions = {};
    gridOptions.enableColumnMenus = false;
    gridOptions.enableFiltering = true;
    gridOptions.enableHorizontalScrollbar =
      uiGridConstants.scrollbars.WHEN_NEEDED;
    gridOptions.enableVerticalScrollbar =
      uiGridConstants.scrollbars.WHEN_NEEDED;
    gridOptions.enableRowSelection = true;
    gridOptions.enableFullRowSelection = true;
    gridOptions.enableRowHeaderSelection = true;
    gridOptions.enableSelectAll = !mThis.disableEdit;
    gridOptions.isRowSelectable = function (row) {
      return !row.entity.unclearable;
    };
    gridOptions.minRowsToShow = 7;
    gridOptions.multiSelect = true;
    gridOptions.resize = false;
    gridOptions.enableColumnResizing = true;
    gridOptions.data = (forDeposit ? mDepositItems : mCheckItems) || [];

    /*****************************************************************************
     * onRegisterApi */
    /**
     * Registers the selection and de-selection of the rows in the grids for
     * reconciliation.
     *
     * @param  gridApi   grid
     *****************************************************************************/
    gridOptions.onRegisterApi = function (gridApi) {
      gridOptions.gridApi = gridApi;

      /*-----------------------------------------------------------------*/
      /* Single selection. For any kind of single selection, even if     */
      /* that results in multiple things being selected after the event. */
      /*-----------------------------------------------------------------*/
      gridApi.selection.on.rowSelectionChanged($scope, function (row) {
        if (!mThis.disableEdit) row.entity.cleared = row.isSelected;
      });

      /*-------------------------------------------------*/
      /* Multi selection. Selections where Shift is used */
      /* to select a range of rows at the same time.     */
      /*-------------------------------------------------*/
      gridApi.selection.on.rowSelectionChangedBatch($scope, function (rows) {
        if (!mThis.disableEdit)
          rows.forEach(function (row) {
            row.entity.cleared = row.isSelected;
          });
      });

      filterItems();
    };

    /*------------------------------------------------------------------*/
    /* Set the column definitions. Both grids have the same definitions */
    /*------------------------------------------------------------------*/
    gridOptions.columnDefs = [
      {
        /*------*/
        /* Date */
        /*------*/
        field: 'entryDate',
        displayName: 'Date',
        cellFilter: 'date:"MM/dd/yyyy"',
        type: 'date',
        filterCellFiltered: true,
        width: 100
      },
      /*------*/
      /* Type */
      /*------*/
      {
        field: 'type',
        filter: forDeposit ? mDepositTypeFilter : mCheckTypeFilter,
        cellTooltip: true,
        width: 100
      },
      /*--------*/
      /* Number */
      /*--------*/
      {
        field: 'docNumber',
        displayName: 'Number',
        width: 90,
        type: 'number',
        labelFunction: docNumberLabel
      },
      /*-------------*/
      /* Description */
      /*-------------*/
      {
        field: 'desc',
        displayName: 'Description',
        cellTooltip: true
      },
      /*--------*/
      /* Amount */
      /*--------*/
      {
        field: 'amount',
        type: 'number',
        cellFilter: 'number:2',
        width: 120
      }
    ]

      /*-------------------------------------------------*/
      /* Sets the class on the unclearable transactions  */
      /* so that they are easily identified in the grid. */
      /*-------------------------------------------------*/
      .map(function (def) {
        def.cellClass = function (
          grid,
          row,
          col,
          rowRenderIndex,
          colRenderIndex
        ) {
          return (
            (col.field == 'amount' ? 'accounting ' : '') +
            (row.entity.unclearable ? 'unclearable' : '')
          );
        };
        return def;
      });

    return gridOptions;
  }

  /*****************************************************************************
   * docNumberLabel */
  /**
   * Labels the docNumber of the <b>item</b>.
   *
   * @param  item  Line item to label.
   *****************************************************************************/
  function docNumberLabel(item) {
    return item.docNumber == '0' ? 'To Print' : item.docNumber;
  }

  /*****************************************************************************
   * filterItems */
  /**
   * Filters the single list of items into debits and credits.
   *****************************************************************************/
  function filterItems() {
    /*-------------------------------------------------------------*/
    /* Separates the statementItems out in to checks and deposits. */
    /*-------------------------------------------------------------*/
    var clearedItems =
      mThis.items.filter(function (item) {
        return item.cleared;
      }) || [];
    getOptionGrid(true).data =
      mThis.items.filter(function (item) {
        return item.isDebit;
      }) || [];
    getOptionGrid(false).data =
      mThis.items.filter(function (item) {
        return !item.isDebit;
      }) || [];

    /*----------------------------------------------------*/
    /* Timeout put here so that the statement information */
    /* on the grid is called before the load. This allows */
    /* the reconciliation in progress to load correctly.  */
    /*----------------------------------------------------*/
    $timeout(function () {
      /*-------------------------------------------------------------*/
      /* If we are disabling any edit functionality, we want to make */
      /* sure the rows have been updated with the initial selections */
      /* from the server, then turn off the selectable row flag.     */
      /*-------------------------------------------------------------*/
      if (mThis.disableEdit) {
        mThis.checkOptions.gridApi.grid.modifyRows(mThis.checkOptions.data);
        mThis.depositOptions.gridApi.grid.modifyRows(mThis.depositOptions.data);

        mThis.checkOptions.isRowSelectable = function (row) {
          return false;
        };
        mThis.depositOptions.isRowSelectable = function (row) {
          return false;
        };

        mThis.checkOptions.noUnselect = true;
        mThis.depositOptions.noUnselect = true;
      }

      /*--------------------------------------------*/
      /* Select the items that are already cleared. */
      /*--------------------------------------------*/
      clearedItems.forEach(function (item) {
        getOptionGrid(item.isDebit).gridApi.selection.selectRow(item);
      });

      window.unsavedChanges = false;
      $scope.$digest();
    });
  }

  /*****************************************************************************
   * getOptionGrid */
  /**
   * Returns the specified option grid.
   *
   * @param  debitGrid  Returns the specified list.
   *                    true=depositOptions, false=checkOptions
   *****************************************************************************/
  function getOptionGrid(debitGrid) {
    return debitGrid ? mThis.depositOptions : mThis.checkOptions;
  }

  /*****************************************************************************
   * onInit */
  /**
   * @ngdoc    method
   * @name     $onInit
   * @methodOf csm.books.BankRecon.controller:BankReconItems
   * @description
   * Initializes the component.
   *****************************************************************************/
  mThis.$onInit = function () {
    /*-----------------------*/
    /* Initialize controller */
    /*-----------------------*/
    mThis.checkOptions = getGridOptions(false);
    mThis.depositOptions = getGridOptions(true);

    setTypeFilters();
  };

  /*****************************************************************************
   * setTypeFilters */
  /**
   * Sets the Type filters for each grid.
   *****************************************************************************/
  function setTypeFilters() {
    /*-------------------------------------------*/
    /* Instantiate the arrays so we can use      */
    /* them as a single variable in the forEach. */
    /*-------------------------------------------*/
    var checkTypeOptions = [];
    var depositTypeOptions = [];

    /*------------------------------------------------------*/
    /* Go through the list of items, and add it to the type */
    /* filter depending on the item type (debit/credit).    */
    /*------------------------------------------------------*/
    angular.forEach(mThis.items, function (item) {
      var typeSelectOptions = item.isDebit
        ? depositTypeOptions
        : checkTypeOptions;

      if (typeSelectOptions.indexOf(item.type) < 0)
        typeSelectOptions.push(item.type);
    });

    /*---------------------------------------------------*/
    /* Map the list of types to the selectOption format. */
    /*---------------------------------------------------*/
    checkTypeOptions = checkTypeOptions
      .map(function (cto) {
        return { value: cto, label: cto };
      })
      .sortOn('label');
    depositTypeOptions = depositTypeOptions
      .map(function (dto) {
        return { value: dto, label: dto };
      })
      .sortOn('label');
    checkTypeOptions.unshift({ value: null, label: '-- All --' });
    depositTypeOptions.unshift({ value: null, label: '-- All --' });

    /*-----------------------------------------------------------*/
    /* Set the finalized lists onto the filters' select options. */
    /*-----------------------------------------------------------*/
    mCheckTypeFilter.selectOptions = checkTypeOptions;
    mDepositTypeFilter.selectOptions = depositTypeOptions;
  }
}
